// Banners (contracts/messaging.md §2): admin cards with an optional image on Home, Arena and Profile, the
// News & events opt-in card among them. Every role reads; editors create, change, pause and delete.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "./client";
import { ACTIONS, APP_LINKS, type AppLink, Content, ImageRef, type MessageAction } from "./messaging";

export const BANNER_PLACEMENTS = ["home", "arena", "profile"] as const;
export const BANNER_STYLES = ["hero", "compact"] as const;
export const BANNER_AUDIENCES = ["all", "free", "pro", "news_off"] as const;
export const BANNER_STATES = ["live", "scheduled", "paused", "ended"] as const;
export type BannerPlacement = (typeof BANNER_PLACEMENTS)[number];
export type BannerStyle = (typeof BANNER_STYLES)[number];
export type BannerAudience = (typeof BANNER_AUDIENCES)[number];
export type BannerState = (typeof BANNER_STATES)[number];

/** Banner actions: every message action (an announcement opens its detail screen). */
export const BANNER_ACTIONS = ACTIONS;
export const PRIORITY_MIN = -1000;
export const PRIORITY_MAX = 1000;

const count = z.number().int().min(0);

export const AdminBanner = z.object({
  id: z.string(),
  placement: z.enum(BANNER_PLACEMENTS),
  style: z.enum(BANNER_STYLES),
  content: Content,
  image: ImageRef.nullable(),
  action: z.enum(ACTIONS),
  link: z.enum(APP_LINKS).nullable(),
  url: z.string().nullable(),
  announcementId: z.string().nullable(),
  audience: z.enum(BANNER_AUDIENCES),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  priority: z.number().int(),
  active: z.boolean(),
  dismissible: z.boolean(),
  state: z.enum(BANNER_STATES),
  /** Unique players; ctr = tapped ÷ shown (null before anyone saw it). */
  stats: z.object({ shown: count, tapped: count, dismissed: count, ctr: z.number().min(0).nullable() }),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminBanner = z.infer<typeof AdminBanner>;
export const AdminBanners = z.object({ items: z.array(AdminBanner) });

/** POST /banners (PATCH takes any subset). `startsAt` left out: now; `endsAt` null: no end. */
export interface BannerDraft {
  placement: BannerPlacement;
  style: BannerStyle;
  content: Content;
  imageId: string | null;
  action: MessageAction;
  link: AppLink | null;
  url: string | null;
  announcementId: string | null;
  audience: BannerAudience;
  startsAt?: string;
  endsAt: string | null;
  priority: number;
  active: boolean;
  dismissible: boolean;
}

export const bannersApi = {
  list: (placement?: BannerPlacement, signal?: AbortSignal) => request(AdminBanners, "GET", "/banners", { query: { placement }, signal }),
  create: (draft: BannerDraft) => request(AdminBanner, "POST", "/banners", { body: draft }),
  update: (id: string, patch: Partial<BannerDraft>) => request(AdminBanner, "PATCH", `/banners/${encodeURIComponent(id)}`, { body: patch }),
  remove: (id: string) => request(z.null(), "DELETE", `/banners/${encodeURIComponent(id)}`),
};

export const bannerKeys = { all: ["banners"] as const, list: () => ["banners", "list"] as const };

export const useBanners = () => useQuery({ queryKey: bannerKeys.list(), queryFn: ({ signal }) => bannersApi.list(undefined, signal) });

function useBannerMutation<V>(fn: (v: V) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: bannerKeys.all });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export const useCreateBanner = () => useBannerMutation((draft: BannerDraft) => bannersApi.create(draft));
export const useUpdateBanner = () => useBannerMutation(({ id, patch }: { id: string; patch: Partial<BannerDraft> }) => bannersApi.update(id, patch));
export const useDeleteBanner = () => useBannerMutation((id: string) => bannersApi.remove(id));

export const PLACEMENT_LABEL: Record<BannerPlacement, string> = { home: "Home", arena: "Arena", profile: "Profile" };
export const STYLE_LABEL: Record<BannerStyle, string> = { hero: "Hero", compact: "Compact" };
export const BANNER_AUDIENCE_LABEL: Record<BannerAudience, string> = {
  all: "Everyone",
  free: "Free players (not Pro)",
  pro: "Pro players",
  news_off: "News & events off",
};
export const STATE_TONE: Record<BannerState, string> = { live: "success", scheduled: "info", paused: "warning", ended: "muted" };
/** Recommended image sizes (contracts/messaging.md "Images"). */
export const BANNER_IMAGE: Record<BannerStyle, { width: number; height: number; hint: string }> = {
  hero: { width: 1200, height: 600, hint: "1200×600 (2:1), shown full width at the top of the card" },
  compact: { width: 400, height: 400, hint: "square, at least 400×400, shown as a thumbnail on the left" },
};
